/**
 * Tier-3 celebration: full-screen rank-up ceremony (design variant C, "Badge
 * Forge"). Plays the forge choreography, then rises text, coin progress toward
 * the next rank and Continue/Share on the design timeline. Sound impact and haptic
 * land on the shatter moment (~950ms). Rank is coin-based (specs/coin-based-ranks).
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Animated,
  Easing,
  Modal,
  Platform,
  Pressable,
  Share,
  Text,
  View,
} from 'react-native';
import * as Haptics from 'expo-haptics';
import { LinearGradient } from 'expo-linear-gradient';
import { soundService } from '../../../shared/services/SoundService';
import { SOUND_IDS } from '../../../shared/constants/sounds.constants';
import type { RankDef } from '../../constants/ranks.constants';
import { getNextRankName, getXpProgress } from '../../helpers/rankRegistry.helper';
import {
  CELEBRATION_COLORS,
  CELEBRATION_TEST_IDS,
  FORGE_TIMELINE,
} from '../../constants/celebrations.constants';
import { BadgeForgeStage } from './BadgeForgeStage';
import { ForgeBurst } from './ForgeBurst';
import { getRankUpCelebrationStyles } from './RankUpCelebration.styles';

const USE_NATIVE = Platform.OS !== 'web';
const DESIGN_EASE = Easing.bezier(0.16, 1, 0.3, 1);

const Rise: React.FC<{ delay: number; children: React.ReactNode }> = ({ delay, children }) => {
  const y = useRef(new Animated.Value(18)).current;
  const op = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(y, { toValue: 0, duration: 500, delay, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
      Animated.timing(op, { toValue: 1, duration: 500, delay, useNativeDriver: USE_NATIVE }),
    ]).start();
    // Mount-only fixed timeline
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Animated.View style={{ opacity: op, transform: [{ translateY: y }], alignSelf: 'stretch', alignItems: 'center' }}>
      {children}
    </Animated.View>
  );
};

export interface RankUpCelebrationProps {
  fromRank: RankDef;
  toRank: RankDef;
  level: number;
  xpCurrent: number;
  xpTarget: number;
  /** Show the Share button (default true). */
  share?: boolean;
  onDismiss: () => void;
  testID?: string;
}

export const RankUpCelebration: React.FC<RankUpCelebrationProps> = ({
  fromRank,
  toRank,
  level,
  xpCurrent,
  xpTarget,
  share = true,
  onDismiss,
  testID,
}) => {
  const styles = useMemo(() => getRankUpCelebrationStyles(), []);
  const nextRankName = getNextRankName(toRank);
  const progress = getXpProgress(xpCurrent, xpTarget);

  const scrimOp = useRef(new Animated.Value(0)).current;
  const barFill = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    void soundService.play(SOUND_IDS.RANK_UP);

    Animated.timing(scrimOp, {
      toValue: 1,
      duration: FORGE_TIMELINE.SCRIM_IN_MS,
      useNativeDriver: USE_NATIVE,
    }).start();

    Animated.timing(barFill, {
      toValue: progress,
      duration: FORGE_TIMELINE.XP_FILL_MS,
      delay: FORGE_TIMELINE.XP_FILL_DELAY_MS,
      easing: DESIGN_EASE,
      useNativeDriver: false,
    }).start();

    const hapticTimer = setTimeout(() => {
      if (Platform.OS !== 'web') {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {
          /* unsupported device */
        });
      }
    }, FORGE_TIMELINE.IMPACT_MS);
    return () => clearTimeout(hapticTimer);
    // Mount-only: one fixed timeline per celebration (host remounts via key)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShare = useCallback(() => {
    Share.share({ message: `I just reached ${toRank.name} on Zporti! 🏆` }).catch(() => {
      /* user cancelled or share unsupported */
    });
  }, [toRank.name]);

  return (
    <Modal
      visible
      transparent
      animationType="none"
      onRequestClose={onDismiss}
      statusBarTranslucent
      presentationStyle="overFullScreen"
      testID={testID ?? CELEBRATION_TEST_IDS.RANK_UP}
    >
      <Animated.View style={[styles.scrim, { opacity: scrimOp }]}>
        <View>
          <BadgeForgeStage fromBadge={fromRank.badge} toBadge={toRank.badge} />
          <ForgeBurst />
        </View>

        <View style={styles.textBlock}>
          <Rise delay={FORGE_TIMELINE.RISE_TRANSITION_MS}>
            <Text style={styles.transitionText} testID={CELEBRATION_TEST_IDS.RANK_UP_TRANSITION}>
              {fromRank.name.toUpperCase()} → {toRank.name.toUpperCase()}
            </Text>
          </Rise>
          <Rise delay={FORGE_TIMELINE.RISE_NAME_MS}>
            <Text style={styles.rankNameText} testID={CELEBRATION_TEST_IDS.RANK_UP_NAME}>
              {toRank.name}
            </Text>
          </Rise>
          <Rise delay={FORGE_TIMELINE.RISE_CHIP_MS}>
            <View style={styles.levelChip}>
              <Text style={styles.levelChipText} testID={CELEBRATION_TEST_IDS.RANK_UP_LEVEL_CHIP}>
                Level {level} · New badge earned 🏆
              </Text>
            </View>
          </Rise>
        </View>

        <Rise delay={FORGE_TIMELINE.RISE_XP_MS}>
          <View style={styles.xpBlock}>
            <View style={styles.barTrack}>
              <Animated.View
                style={[
                  styles.barFill,
                  { width: barFill.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) },
                ]}
              >
                <LinearGradient
                  colors={[CELEBRATION_COLORS.PURPLE, CELEBRATION_COLORS.MINT]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{ flex: 1 }}
                />
              </Animated.View>
            </View>
            <View style={styles.barLabels}>
              <Text style={styles.barLabelText} testID={CELEBRATION_TEST_IDS.RANK_UP_XP}>
                {xpCurrent.toLocaleString()} / {xpTarget.toLocaleString()} coins
              </Text>
              {nextRankName !== null && (
                <Text style={styles.barLabelText} testID={CELEBRATION_TEST_IDS.RANK_UP_NEXT}>
                  Next: {nextRankName}
                </Text>
              )}
            </View>
          </View>
        </Rise>

        <Rise delay={FORGE_TIMELINE.RISE_BUTTONS_MS}>
          <View style={styles.buttons}>
            <Pressable
              style={({ pressed }) => [styles.continueBtn, pressed && styles.btnPressed]}
              onPress={onDismiss}
              accessibilityRole="button"
              accessibilityLabel="Continue"
              testID={CELEBRATION_TEST_IDS.RANK_UP_CONTINUE}
            >
              <Text style={styles.continueText}>Continue</Text>
            </Pressable>
            {share && (
              <Pressable
                style={({ pressed }) => [styles.shareBtn, pressed && styles.btnPressed]}
                onPress={handleShare}
                accessibilityRole="button"
                accessibilityLabel="Share your new rank"
                testID={CELEBRATION_TEST_IDS.RANK_UP_SHARE}
              >
                <Text style={styles.shareText}>Share</Text>
              </Pressable>
            )}
          </View>
        </Rise>
      </Animated.View>
    </Modal>
  );
};
